import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, User } from 'lucide-react';
import CivicButton from './CivicButton';
import { useApp } from '../lib/AppContext';

interface AuthRequiredModalProps {
  isOpen: boolean;
  onClose: () => void;
  message?: string;
}

export default function AuthRequiredModal({ isOpen, onClose, message }: AuthRequiredModalProps) {
  const { navigateTo } = useApp();

  useEffect(() => {
    if (!isOpen) return;

    const originalOverflow = document.body.style.overflow;
    const originalPaddingRight = document.body.style.paddingRight;

    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = `${scrollbarWidth}px`;

    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEsc);

    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.paddingRight = originalPaddingRight;
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleCreateAccount = () => {
    onClose();
    navigateTo('auth', undefined, { authMode: 'signup' });
  };

  const handleSignIn = () => {
    onClose();
    navigateTo('auth', undefined, { authMode: 'signin' });
  };

  const modalContent = (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center"
      style={{
        background: 'rgba(0, 0, 0, 0.55)',
        backdropFilter: 'blur(4px)',
        padding: 'max(16px, env(safe-area-inset-top)) 16px max(16px, env(safe-area-inset-bottom)) 16px',
      }}
      onClick={onClose}
    >
      <div
        className="relative w-full bg-[#141414] rounded-2xl border p-6"
        style={{
          maxWidth: 'min(92vw, 448px)',
          maxHeight: 'min(90vh, calc(100vh - 32px))',
          borderColor: 'rgba(64, 224, 208, 0.28)',
          boxShadow: '0px 12px 28px 3px rgba(0, 0, 0, 0.52), 0px 2px 8px 1px rgba(0, 0, 0, 0.16)',
          overflow: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 hover:bg-[#1A1A1A] rounded-lg transition-colors z-10"
          aria-label="Close"
        >
          <X className="w-5 h-5 text-[#808080] hover:text-[#A0A0A0]" />
        </button>

        <div className="flex items-center gap-3 mb-4">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: 'rgba(64, 224, 208, 0.12)' }}
          >
            <User className="w-5 h-5" style={{ color: '#40E0D0' }} />
          </div>
          <h2
            className="text-[18px] font-semibold text-[#F5F5F5] flex-1"
            style={{ letterSpacing: '-0.005em' }}
          >
            Create your account
          </h2>
        </div>

        <p className="text-[14px] text-[#C0C0C0] leading-relaxed mb-6">
          {message || "You need an account to use this feature."}
        </p>

        <div className="flex flex-col gap-3">
          <CivicButton
            variant="primary"
            fullWidth
            onClick={handleCreateAccount}
          >
            Create Account
          </CivicButton>

          <CivicButton
            variant="secondary"
            fullWidth
            onClick={handleSignIn}
          >
            Sign In
          </CivicButton>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
